import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { tabStorage } from "./tabStorage";
import type { AssistantPlan } from "@/api/form-builder/formAssistant.types";
import type {
  FormType,
  VisitCategory,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";

/**
 * What the form assistant starts with when the form builder opens:
 * - a new form (no formId) with the plan approved in the assignments assistant;
 * - a published form (formId) with the change asked for there, which it proposes
 *   in the preview for the person to apply or discard. Nothing is saved for them.
 */
export interface BuildHandoff {
  formId?: string;
  brief: string;
  plan?: AssistantPlan;
  /** What the person typed, shown in the chat; the brief only goes to the model. */
  said?: string;
}

/** Where to go back once the new form is published, and which slot it is for. */
export interface AssignHandoff {
  tenantId: string;
  workOrderType: VisitCategory;
  formType: FormType;
  slotLabel: string;
  /** Narrows the slot like the page it came from: client, brand, trade, subtrade. */
  scope: { clientId?: string; brandId?: string; tradeId?: number; subtradeId?: number };
  returnTo: { href: string };
}

/** Where to go back once a changed published form is published again. */
export interface ReturnHandoff {
  formId: string;
  formName: string;
  tenantId: string;
  returnTo: { href: string };
}

interface AssistantHandoffState {
  build: BuildHandoff | null;
  assign: AssignHandoff | null;
  back: ReturnHandoff | null;
  start: (handoff: { build: BuildHandoff; assign: AssignHandoff }) => void;
  startChange: (handoff: { build: BuildHandoff; back: ReturnHandoff }) => void;
  /** Taken only by the form it was meant for: `undefined` is a new form. */
  takeBuild: (formId: string | undefined) => BuildHandoff | null;
  /** The way back for the form the editor opened: a new form, or that published form. */
  wayBack: (formId: string | undefined) => {
    assign: AssignHandoff | null;
    back: ReturnHandoff | null;
  };
  /** Kept while that form stays open, so a reload still knows where to go back. */
  hold: (handoff: { assign: AssignHandoff | null; back: ReturnHandoff | null }) => void;
  /** The form was left: a later form never inherits its way back. */
  release: () => void;
}

const initializer = (
  set: (partial: Partial<AssistantHandoffState>) => void,
  get: () => AssistantHandoffState,
): AssistantHandoffState => ({
  build: null,
  assign: null,
  back: null,
  start: ({ build, assign }) => set({ build, assign, back: null }),
  startChange: ({ build, back }) => set({ build, back, assign: null }),
  takeBuild: (formId) => {
    const { build } = get();
    if (!build || build.formId !== formId) return null;
    set({ build: null });
    return build;
  },
  wayBack: (formId) => {
    const { assign, back } = get();
    return formId
      ? { assign: null, back: back?.formId === formId ? back : null }
      : { assign, back: null };
  },
  hold: ({ assign, back }) => set({ assign, back }),
  release: () => set({ assign: null, back: null }),
});

export function createAssistantHandoffStore(persistAs?: string) {
  if (!persistAs) return create<AssistantHandoffState>()(initializer);
  // Per browser tab: a reload keeps the hand-off, another tab starts clean.
  return create<AssistantHandoffState>()(
    persist(initializer, {
      name: persistAs,
      storage: createJSONStorage(() => tabStorage),
      partialize: ({ build, assign, back }) => ({ build, assign, back }),
    }),
  );
}

// The build is taken once by the form builder; the way back is held by the editor
// while that form is open and released when it is left.
export const useAssistantHandoffStore = createAssistantHandoffStore(
  "form-builder-assistant-handoff",
);
